import React, { useEffect, useState } from "react";
import { ToastContainer, toast } from "react-toastify";
import { Button, Modal, Row, Col, Form, OverlayTrigger } from "react-bootstrap";
import { MdEdit, MdAddCircle, MdFilterList, MdDelete } from "react-icons/md";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Tooltip } from "@mui/material";
import loading from "../../../assets/images/common/loading.gif";
import AppDataGrid from "../../../components/common/AppDataGrid";

const FinancialYear = () => {
  const navigate = useNavigate();
  const baseUrl = process.env.REACT_APP_API_BASE_URL;

  // helper to convert dates (or datetimes) to dd-mm-yyyy for display
  const formatDate = (datetime) => {
    if (!datetime) return "";
    const date = new Date(datetime);
    if (isNaN(date)) return "";
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${day}-${month}-${year}`;
  };

  const userData = sessionStorage.getItem("user");
  const userObj = userData ? JSON.parse(userData) : {};

  const [years, setYears] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [isLoading, setIsLoading] = useState(true);

  // Filter modal state (same as Roles)
  const [showFilterModal, setShowFilterModal] = useState(false);
  const handleShowFilterModal = () => setShowFilterModal(true);
  const handleCloseFilterModal = () => setShowFilterModal(false);

  const [filter, setFilter] = useState({
    fy_name: "",
    start_date: "",
    end_date: "",
    status: "", // Active / Inactive (optional)
    action: "FILTER",
  });

  // ✅ READ
  const fetchYears = async () => {
    try {
      setIsLoading(true);
      // Adjust payload as per your backend format
      const res = await axios.post(`${baseUrl}/financialyearprocedure/`, {
        action: "READ",
       
        school_id: userObj.school_id
       
      });
      setYears(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error(err);
      setYears([]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchYears();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ✅ Edit
  const handleEditClick = (fy_id) => {
    const fyToEdit = years.find((x) => x.fy_id === fy_id);
    if (fyToEdit) {
      navigate("/finance/financialyear/add", { state: { year: fyToEdit } });
    } else {
      toast.error("Financial year not found");
    }
  };

  // ✅ Delete / Inactive
  const handleDeleteClick = async (fy_id) => {
    const confirmDelete = window.confirm("Are you sure you want to change the status?");
    if (!confirmDelete) return;

    try {
      await axios.post(`${baseUrl}/financialyearprocedure/`, {
        action: "DELETE",
        fy_id,
        school_id: userObj.school_id,
        fy_name: null,
        start_date: null,
        end_date: null,
        is_active: null,
        is_closed: null,
        closed_on: null,
        created_by: userObj.user_name,
      });
      toast.success("Record Set to InActive");
      fetchYears();
    } catch (error) {
      console.error(error);
      toast.error("Failed to change status");
    }
  };

  // ✅ Search filter (same logic used in Roles)
  const searchableColumns = [
    (row) => row.fy_name,
    (row) => formatDate(row.start_date),
(row) => formatDate(row.end_date),
    (row) => (row.is_active ? "Active" : "Inactive"),
    (row) => row.opening_balance,
    (row) => row.current_balance,
    (row) => row.total_income,
    (row) => row.total_expense,
  ];

  const filteredRecords = (years || []).filter((item) =>
    searchableColumns.some((selector) => {
      const value = selector(item);
      const stringValue = String(value || "").toUpperCase().replace(/[-\s]+/g, "");
      const normalizedQuery = searchQuery.toUpperCase().replace(/[-\s]+/g, "");
      return stringValue.includes(normalizedQuery);
    })
  );

  // ✅ DataGrid columns (same format as Roles)
  const columns = [
    {
      field: "fy_name",
      headerName: "Financial Year",
      flex: 1,
      headerAlign: "center",
      align: "center",
      renderCell: (params) => (
        <Tooltip title={params.value || ""}>
          <span>{params.value}</span>
        </Tooltip>
      ),
      sortable: true,
    },
    {
      field: "start_date",
      headerName: "Start Date",
      flex: 1,
      headerAlign: "center",
      align: "center",
      renderCell: (params) => {
        const formatted = formatDate(params.value);
        return (
          <Tooltip title={formatted}>
            <span>{formatted}</span>
          </Tooltip>
        );
      },
      sortable: true,
    },
    {
      field: "end_date",
      headerName: "End Date",
      flex: 1,
      headerAlign: "center",
      align: "center",
      renderCell: (params) => {
        const formatted = formatDate(params.value);
        return (
          <Tooltip title={formatted}>
            <span>{formatted}</span>
          </Tooltip>
        );
      },
      sortable: true,
    },
    {
      field: "opening_balance",
      headerName: "Opening Balance",
      flex: 1,
      headerAlign: "center",
      align: "center",
      renderCell: (params) => (
        <Tooltip title={params.value != null ? params.value : ""}>
          <span>{params.value != null ? params.value : ""}</span>
        </Tooltip>
      ),
      sortable: true,
    },
    {
      field: "current_balance",
      headerName: "Current Balance",
      flex: 1,
      headerAlign: "center",
      align: "center",
      renderCell: (params) => (
        <Tooltip title={params.value != null ? params.value : ""}>
          <span>{params.value != null ? params.value : ""}</span>
        </Tooltip>
      ),
      sortable: true,
    },
    {
      field: "total_income",
      headerName: "Total Income",
      flex: 1,
      headerAlign: "center",
      align: "center",
      renderCell: (params) => (
        <Tooltip title={params.value != null ? params.value : ""}>
          <span>{params.value != null ? params.value : ""}</span>
        </Tooltip>
      ),
      sortable: true,
    },
    {
      field: "total_expense",
      headerName: "Total Expense",
      flex: 1,
      headerAlign: "center",
      align: "center",
      renderCell: (params) => (
        <Tooltip title={params.value != null ? params.value : ""}>
          <span>{params.value != null ? params.value : ""}</span>
        </Tooltip>
      ),
      sortable: true,
    },
    {
      field: "is_active",
      headerName: "Status",
      flex: 1,
      headerAlign: "center",
      align: "center",
      renderCell: (params) => {
        const label = params.value ? "Active" : "Inactive";
        return (
          <Tooltip title={label}>
            <span>{label}</span>
          </Tooltip>
        );
      },
      sortable: true,
    },
    {
      field: "actions",
      headerName: "Actions",
      flex: 1,
      headerAlign: "center",
      align: "center",
      renderCell: (params) => (
        <div className="tableActions">
          <span className="commonActionIcons" onClick={() => handleEditClick(params.row.fy_id)}>
            <MdEdit />
          </span>
          <span className="commonActionIcons" onClick={() => handleDeleteClick(params.row.fy_id)}>
            <MdDelete />
          </span>
        </div>
      ),
    },
  ];

  // ✅ Filter submit (optional – same as Roles)
  const handleFilterSubmit = async (e) => {
    e.preventDefault();
    try {
      setIsLoading(true);

      const payload = {
        action: "FILTER",
        school_id: userObj.school_id,
        fy_name: filter.fy_name || null,
        start_date: filter.start_date || null,
        end_date: filter.end_date || null,
        status: filter.status || null,
      };

      const res = await axios.post(`${baseUrl}/financialyearprocedure/`, payload, {
        headers: { "Content-Type": "application/json" },
      });

      setYears(Array.isArray(res.data) ? res.data : []);
      setShowFilterModal(false);
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleReset = async (e) => {
    e.preventDefault();
    setFilter({ fy_name: "", start_date: "", end_date: "", status: "", action: "FILTER" });
    setShowFilterModal(true);
    fetchYears();
  };

  return (
    <div className="pageBody">
      <ToastContainer />

      <div className="commonDataTableHead">
        <div className="d-flex justify-content-between align-items-center w-100">
          <div className="d-flex align-items-center" style={{ gap: "10px" }}>
            <h6 className="commonTableTitle">Financial Year</h6>
          </div>

          <div>
            <input
              type="text"
              placeholder="Search..."
              value={searchQuery}
              className="searchInput"
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>

          <div className="d-flex align-items-center" style={{ gap: 6 }}>
            <OverlayTrigger placement="top" overlay={<Tooltip id="tooltip-top">Filter</Tooltip>}>
              <Button className="secondaryBtn" variant="secondary" onClick={handleShowFilterModal}>
                <MdFilterList />
              </Button>
            </OverlayTrigger>

            <OverlayTrigger placement="top" overlay={<Tooltip id="tooltip-top">Add</Tooltip>}>
              <Button className="primaryBtn" variant="primary" onClick={() => navigate("/finance/financialyear/add")}>
                <MdAddCircle />
              </Button>
            </OverlayTrigger>
          </div>
        </div>
      </div>

      <div className="commonTable">
        <div className="tableBody">
          {isLoading ? (
            <div className="loadingContainer">
              <img src={loading} alt="Loading..." className="loadingGif" />
            </div>
          ) : (
            <AppDataGrid
              rows={filteredRecords.length ? filteredRecords : []}
              columns={columns}
              getRowId={(row) => row.fy_id}
              pageSizeOptions={[10, 20, 50]}
              showToolbar={false}
              pagination
              density="compact"
              maxHeight={550}
            />
          )}
        </div>
      </div>

      {/* Filter Modal (same design as Roles) */}
      <Modal show={showFilterModal} onHide={handleCloseFilterModal} className="commonFilterModal">
        <Modal.Header closeButton className="modalHeaderFixed">
          <Modal.Title>Filter</Modal.Title>
        </Modal.Header>

        <Modal.Body className="modalBodyScrollable">
          <Row>
            <Col xs={12}>
              <div className="commonInput">
                <Form.Group controlId="fy_name">
                  <Form.Label>Financial Year</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Enter Financial Year (e.g. 2025-2026)"
                    value={filter.fy_name}
                    onChange={(e) => {
                      const val = e.target.value;
                      if (/^[0-9-]{0,20}$/.test(val)) {
                        setFilter({ ...filter, fy_name: val });
                      }
                    }}
                  />
                </Form.Group>
              </div>
            </Col>

            <Col xs={12}>
              <div className="commonInput">
                <Form.Group controlId="start_date">
                  <Form.Label>Start Date</Form.Label>
                  <Form.Control
                    type="date"
                    value={filter.start_date}
                    onChange={(e) => setFilter({ ...filter, start_date: e.target.value })}
                  />
                </Form.Group>
              </div>
            </Col>

            <Col xs={12} >
              <div className="commonInput">
                <Form.Group controlId="end_date">
                  <Form.Label>End Date</Form.Label>
                  <Form.Control
                    type="date"
                    value={filter.end_date}
                    onChange={(e) => setFilter({ ...filter, end_date: e.target.value })}
                  />
                </Form.Group>
              </div>
            </Col>

            
          </Row>
        </Modal.Body>

        <Modal.Footer className="modalFooterFixed">
          <Button variant="secondary" className="btn-info clearBtn me-2" onClick={handleReset}>
            Reset
          </Button>
          <div>
            <Button variant="secondary" className="btn-danger secondaryBtn me-2" onClick={handleCloseFilterModal}>
              Cancel
            </Button>
            <Button variant="primary" className="btn-success primaryBtn" onClick={handleFilterSubmit}>
              Search
            </Button>
          </div>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default FinancialYear;